// "Add CrewZ to your home screen" banner — phones only, never during a workout.
// Android/Chrome: uses the captured `beforeinstallprompt` event (one-tap install).
// iOS Safari: no install API, so it shows the Share → Add to Home Screen steps.
// Dismiss = hidden for 14 days; installed / standalone = never shown.
import { useEffect, useState } from 'react';
import { X, Share, PlusSquare, Download } from 'lucide-react';
import { useWorkout } from '@/contexts/WorkoutContext';

const DISMISS_KEY = 'crewz_install_dismissed_at';
const DISMISS_DAYS = 14;
const SHOW_DELAY_MS = 8000; // after the splash, once the rower has settled in

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isPhone = () => /Android|iPhone|iPod/i.test(navigator.userAgent) || (window.matchMedia?.('(pointer: coarse)').matches && window.innerWidth < 768);
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent) && !window.MSStream;
const recentlyDismissed = () => {
  const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
  return t && Date.now() - t < DISMISS_DAYS * 864e5;
};

export default function InstallPrompt() {
  const { workoutActive } = useWorkout();
  const [deferred, setDeferred] = useState(null);
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return isStandalone() || !isPhone() || recentlyDismissed(); } catch { return true; }
  });

  useEffect(() => {
    if (hidden) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setHidden(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    const t = setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => {
      clearTimeout(t);
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [hidden]);

  const ios = isIOS();
  // Android needs the captured prompt; iOS shows manual steps.
  if (hidden || !ready || workoutActive || (!ios && !deferred)) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setHidden(true);
  };
  const install = async () => {
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    if (outcome === 'accepted') setHidden(true); else dismiss();
  };

  return (
    <div
      className="fixed inset-x-3 z-[60] crewz-install-in"
      style={{ bottom: 'max(env(safe-area-inset-bottom, 0px), 12px)' }}
      role="dialog"
      aria-label="Add CrewZ to your home screen"
      data-testid="install-prompt"
    >
      <style>{`
        .crewz-install-in { animation: crewz-install-up 420ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        @keyframes crewz-install-up { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .crewz-install-in { animation: none; } }
      `}</style>
      <div className="flex items-center gap-3 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-xl p-3">
        <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-zinc-900 dark:text-white leading-tight">Add CrewZ to your home screen</p>
          {ios ? (
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug" data-testid="install-prompt-ios-steps">
              Tap <Share className="inline w-3.5 h-3.5 -mt-0.5" aria-label="Share" /> then{' '}
              <span className="font-semibold whitespace-nowrap"><PlusSquare className="inline w-3.5 h-3.5 -mt-0.5" /> Add to Home Screen</span>
            </p>
          ) : (
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-snug">Launch it like an app — full screen, one tap from the dock.</p>
          )}
        </div>
        {!ios && (
          <button
            type="button"
            onClick={install}
            className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1565C0] dark:bg-[#4A9EEB] text-white text-xs font-bold uppercase tracking-wider transition-transform active:scale-95"
            data-testid="install-prompt-install-button"
          >
            <Download className="w-3.5 h-3.5" /> Install
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Not now"
          className="shrink-0 p-1.5 rounded-full text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          data-testid="install-prompt-dismiss-button"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
